/**
 * HeroCarousel — Swipeable hero images with native scroll-snap.
 * No dots, no arrows, no overlays. Subtle right-edge fade hints at more images.
 * Overlay (title etc.) is rendered by the parent above this component.
 *
 * Supports imperative scrollToIndex via callback ref pattern.
 */
import { useRef, useCallback, useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface HeroImage {
  id: string;
  src: string;
  alt?: string;
}

export interface HeroCarouselHandle {
  scrollToIndex: (index: number) => void;
}

interface HeroCarouselProps {
  images: HeroImage[];
  className?: string;
  /** Called when user swipes to a new image (debounced). */
  onIndexChange?: (index: number) => void;
  /** Controlled active index — carousel scrolls to this when it changes externally. */
  activeIndex?: number;
}

export const HeroCarousel = forwardRef<HeroCarouselHandle, HeroCarouselProps>(
  function HeroCarousel({ images, className, onIndexChange, activeIndex }, ref) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isExternalScroll = useRef(false);
    const hasMultiple = images.length > 1;

    const scrollToIndex = useCallback((index: number) => {
      const el = scrollRef.current;
      if (!el) return;
      isExternalScroll.current = true;
      el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
      setCurrentIndex(index);
      // Reset flag after scroll settles
      setTimeout(() => { isExternalScroll.current = false; }, 350);
    }, []);

    useImperativeHandle(ref, () => ({ scrollToIndex }), [scrollToIndex]);

    // React to controlled activeIndex changes
    useEffect(() => {
      if (activeIndex !== undefined && activeIndex !== currentIndex) {
        scrollToIndex(activeIndex);
      }
      // Only react to activeIndex changes, not currentIndex
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, scrollToIndex]);

    // Debounced scroll-end detection
    const handleScroll = useCallback(() => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const el = scrollRef.current;
        if (!el) return;
        const w = el.clientWidth;
        if (w === 0) return;
        const idx = Math.round(el.scrollLeft / w);
        setCurrentIndex((prev) => {
          if (idx !== prev) {
            if (!isExternalScroll.current) {
              onIndexChange?.(idx);
            }
            return idx;
          }
          return prev;
        });
      }, 120);
    }, [onIndexChange]);

    useEffect(() => {
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }, []);

    // Single image — identical to existing hero
    if (images.length === 0) return null;

    if (images.length === 1) {
      return (
        <div className={cn("relative w-full overflow-hidden", className)}>
          <img
            src={images[0].src}
            alt={images[0].alt ?? ""}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
        </div>
      );
    }

    return (
      <div className={cn("relative w-full overflow-hidden", className)}>
        {/* Scroll container */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
          style={{
            WebkitOverflowScrolling: "touch",
            scrollBehavior: "auto",
            touchAction: "pan-x pan-y",
          }}
          tabIndex={0}
          role="region"
          aria-label="Bilder för platsen"
          aria-roledescription="carousel"
          data-testid="place-hero-scroll"
        >
          <style>{`
            @media (prefers-reduced-motion: no-preference) {
              .hero-carousel-smooth { scroll-behavior: smooth; }
            }
            .scrollbar-hide::-webkit-scrollbar { display: none; }
            .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
          `}</style>
          {images.map((img, i) => (
            <div
              key={img.id}
              className="flex-shrink-0 w-full h-full snap-start relative"
              role="group"
              aria-roledescription="slide"
              aria-label={`Bild ${i + 1} av ${images.length}`}
              data-testid="place-hero-slide"
              data-index={i}
            >
              <img
                src={img.src}
                alt={img.alt ?? ""}
                className="absolute inset-0 w-full h-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
            </div>
          ))}
        </div>

        {/* Subtle right-edge fade (only when more images exist to the right) */}
        {hasMultiple && currentIndex < images.length - 1 && (
          <div
            className="absolute top-0 right-0 bottom-0 w-12 pointer-events-none"
            style={{
              background:
                "linear-gradient(to right, transparent, rgba(0,0,0,0.12))",
            }}
            aria-hidden="true"
          />
        )}

        {/* Subtle mobile index indicator — "1 / N" */}
        {hasMultiple && (
          <div
            className="absolute top-3 right-3 md:hidden px-2 py-0.5 rounded-full bg-black/30 backdrop-blur-sm pointer-events-none"
            aria-hidden="true"
          >
            <span data-testid="place-hero-counter" className="text-[11px] text-white/70 font-medium tabular-nums">
              {currentIndex + 1} / {images.length}
            </span>
          </div>
        )}
      </div>
    );
  }
);
